/* eslint-disable max-classes-per-file */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import { AnalyticsEvent, ConsentPurpose } from '@rover/types/src/Analytics';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

export type EventTags = {
  cardTreatment: SearchCardTreatment;
  source: string;
};

type EventTagsWithRank =
  | EventTags & {
      rank: string;
    };

export type MaximalCardEventTags = EventTagsWithRank & {
  personOpk: string;
};

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks on "Has low acceptance rate" checkbox inside Other filter in search page.
 *
 * &nbsp;
 */
export class SearchFilterLowBookingCheckboxClickEvent extends AnalyticsEvent {
  eventName = 'search-filter-low-booking-checkbox';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: { checked: boolean }) {
    super(eventProperties, { checked: eventProperties.checked });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user hovers over the info icon next to "Has low acceptance rate" filter on search page.
 *
 * &nbsp;
 */
export class SearchFilterLowBookingTooltipViewedEvent extends AnalyticsEvent {
  eventName = 'low-booking-rate-tooltip-viewed';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a sitter card appears on the search page when the user scrolls down.
 *
 * &nbsp;
 */
export class SearchResultsScrollDepthEvent extends AnalyticsEvent {
  eventName = 'search-scroll-depth';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: EventTagsWithRank) {
    const observabilityTags = {
      cardTreatment: eventProperties.cardTreatment,
      source: eventProperties.source,
    };
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when the user clicks the Background Check link on the search page.
 *
 * &nbsp;
 */
export class SearchBackgroundCheckCalloutClickEvent extends AnalyticsEvent {
  eventName = 'background-check-callout-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: EventTags & { badgeSlug: string }) {
    const observabilityTags = {
      cardTreatment: eventProperties.cardTreatment,
      source: eventProperties.source,
      badgeSlug: eventProperties.badgeSlug,
    };
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks a sitter card on the search page.
 *
 * &nbsp;
 */
export class SearchCardClickedEvent extends AnalyticsEvent {
  eventName = 'search-card-clicked';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: EventTagsWithRank) {
    const observabilityTags = {
      cardTreatment: eventProperties.cardTreatment,
      source: eventProperties.source,
      rank: eventProperties.rank,
    };
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Abstract class for the search results event classes.
 *
 * &nbsp;
 */
abstract class SearchResultAnalyticsEvent extends AnalyticsEvent {
  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(eventProperties: MaximalCardEventTags) {
    const observabilityTags = {
      cardTreatment: eventProperties.cardTreatment,
      source: eventProperties.source,
      rank: eventProperties.rank,
      personOpk: eventProperties.personOpk,
    };
    super(eventProperties, observabilityTags);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user scrolls through the inline image gallery in the search cards.
 *
 * &nbsp;
 */
export class SearchResultGalleryScrollEvent extends SearchResultAnalyticsEvent {
  eventName = 'search-result-gallery-scroll';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks the inline image gallery in the search cards.
 *
 * &nbsp;
 */
export class SearchResultGalleryClickEvent extends SearchResultAnalyticsEvent {
  eventName = 'search-result-gallery-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks the "contact sitter" CTA in the search cards.
 *
 * &nbsp;
 */
export class SearchResultCardContactClickEvent extends SearchResultAnalyticsEvent {
  eventName = 'search-result-card-contact-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks the "see sitter profile" CTA in the search cards.
 *
 * &nbsp;
 */
export class SearchResultCardViewProfileClickEvent extends SearchResultAnalyticsEvent {
  eventName = 'search-result-card-view-profile-click';
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when user clicks the see sitter reviews in the search cards.
 *
 * &nbsp;
 */
export class SearchResultReviewsLinkClickEvent extends SearchResultAnalyticsEvent {
  eventName = 'search-result-reviews-link-click';
}
